import React, { Suspense, lazy, useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Loader } from '@scuf/common';
import { RouteConstants, TabConstants } from './../constants/constants';
import CheckUser from './../components/AppRoutes/CheckUser';
import SamplePage from './../pages/samplePage/SamplePage';
import OrphanPage from './../pages/OrphanPage/OrphanPage';

import EmptyDashboard from './../pages/dashboard/EmptyDashboard';
import AnomalyDetails from './../components/OpportunityModal/AnomalyDetails';
import { OverviewPermisson } from './../components/Permissions/OverviewPermission';
import { CallbackRoute } from 'src/components/AppRoutes/CallbackRoute';
import { LoggedOutComponent } from '@forge/sso-client/dist';
import { useSelector } from 'react-redux';
import { getMenuList } from 'src/slices/onBoardingSlice';
import LandingPage from 'src/pages/LandingPage/LandingPage';
import EditContextList from 'src/pages/contextPage/EditContextList';

const OnBoardingView = lazy(
  () => import('./../pages/OnBoarding/OnBoardingView')
);
const PageNotFound = lazy(() => import('./../pages/404/pageNotFound'));
const DashboardView = lazy(() => import('./../pages/dashboard/DashboardView'));
const ForbiddenUser = lazy(() => import('./../pages/forbidden/ForbiddenUser'));
const UnavailablePage = lazy(
  () => import('./../pages/unavailable/UnavailablePage')
);
const ManageProfileView = lazy(
  () => import('./../pages/ManageProfile/ManageProfileView')
);
const OpportunityPage = lazy(
  () => import('./../pages/allOpportunity/OpportunityPage')
);
const OverviewPage = lazy(() => import('./../pages/overview/OverviewPage'));
const TableauView = lazy(() => import('./../pages/tableau/TableauView'));
const ModelReview = lazy(() => import('./../pages/modelReview/ModelReview'));

const ContextPage = lazy(() => import('../pages/contextPage/ContextPage'));
const DataGpt = lazy(() => import('../pages/datagpt/DataGpt'));
const CamsGpt = lazy(() => import('../pages/camsgpt/CamsGpt'));

const MetricDashboard = lazy(() => import('../pages/metricsDashboard/MetricsDashboard'));

const genRouter = (routeKey: string) => {
  switch (routeKey) {
    case TabConstants.OVERVIEW:
      return {
        path: RouteConstants.OVERVIEW_RELATIVE_PATH,
        element: (
          <OverviewPermisson>
            <OverviewPage />
          </OverviewPermisson>
        ),
      };
    case TabConstants.ANOMALIES:
      return {
        path: RouteConstants.ANOMALY_RELATIVE_PATH,
        element: <OpportunityPage />,
      };
    case TabConstants.SAMPLE:
      return {
        path: RouteConstants.SAMPLE_RELATIVE_PATH,
        element: <SamplePage />,
      };
    case TabConstants.ORPHAN:
      return {
        path: RouteConstants.ORPHAN_RELATIVE_PATH,
        element: <OrphanPage />,
      };
    case TabConstants.CONTEXT:
      return { path: RouteConstants.CONTEXT, element: <ContextPage /> };
    case TabConstants.DATA_GPT:
      return { path: RouteConstants.DATA_GPT, element: <DataGpt /> };
    case TabConstants.CAMS_GPT:
        return { path: RouteConstants.CAMS_GPT, element: <CamsGpt /> };
    case TabConstants.CUSTOM_STRATEGY:
      return { path: RouteConstants.CUSTOM_STRATEGY, element: <TableauView /> };
    case TabConstants.MODEL_REVIEW:
        return { path: RouteConstants.MODEL_REVIEW, element:  <ModelReview/> };
    // case TabConstants.MERTIC_DASHBOARD:
    //     return { path: RouteConstants.MERTIC_DASHBOARD, element:  <MetricDashboard/> };
  }
};

const AppRouter = ({ Wrapper }: any) => {
  const menus: { name: string }[] = useSelector(getMenuList);

  const routerList = useMemo(
    () => menus.map(({ name }) => genRouter(name)),
    [menus]
  );

  return (
    <Routes>
      <Route path={RouteConstants.CALLBACK} element={<CallbackRoute />} />
      <Route path={RouteConstants.LANDING_PAGE} element={<Wrapper><LandingPage/></Wrapper>} />
      <Route
        path={RouteConstants.HOME}
        element={
          <Wrapper>
            <CheckUser />
          </Wrapper>
        }
      />
      <Route
        path={RouteConstants.DASHBOARD}
        element={
          <Wrapper>
            <DashboardView />
          </Wrapper>
        }>
        {routerList.map((routerProps, i) => (
          <Route key={i} {...routerProps} />
        ))}
        {routerList[0] && (
          <Route index element={<Navigate to={routerList[0]?.path} />} />
        )}
        <Route path='*' element={<PageNotFound />} />
      </Route>

      <Route
        path={RouteConstants.ANOMALY_DETAILS}
        element={
          <Wrapper>
            <AnomalyDetails />
          </Wrapper>
        }
      />
      <Route
        path={RouteConstants.ONBOARDING}
        element={
          <Wrapper>
            <OnBoardingView />
          </Wrapper>
        }
      />
      <Route
        path={RouteConstants.FORBIDDEN}
        element={
          <Wrapper>
            <ForbiddenUser />
          </Wrapper>
        }
      />
      <Route
        path={RouteConstants.UNAVAILABLE}
        element={
          <Wrapper>
            <UnavailablePage />
          </Wrapper>
        }
      />
      <Route
        path={RouteConstants.LOGOUT}
        element={
          <Wrapper>
            <LoggedOutComponent autoRenew={true} loadingComponent={Loader} />
          </Wrapper>
        }
      />
      <Route
        path={RouteConstants.MANAGE_PROFILE}
        element={
          <Wrapper>
            <Suspense fallback={<Loader loading={true} text='Loading...' />}>
              <ManageProfileView />
            </Suspense>
          </Wrapper>
        }
      />

<Route
        path={RouteConstants.CONTEXT_EDIT}
        element={
          <Wrapper>
            <Suspense fallback={<Loader loading={true} text='Loading...' />}>
              <EditContextList />
            </Suspense>
          </Wrapper>
        }
      />
       <Route
        path={RouteConstants.CONTEXT_EDIT_MODEL}
        element={
          <Wrapper>
            <Suspense fallback={<Loader loading={true} text='Loading...' />}>
              <EditContextList />
            </Suspense>
          </Wrapper>
        }
      />

      <Route
        path={RouteConstants.PAGE_NOT_FOUND}
        element={
          <Suspense fallback={<EmptyDashboard />}>
            <PageNotFound />
          </Suspense>
        }
      />
      <Route
        path='*'
        element={<Navigate to={RouteConstants.PAGE_NOT_FOUND} />}
      />
    </Routes>
  );
};

export default AppRouter;
